import { useQuery } from '@apollo/client'
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    TablePagination,
    Typography,
} from '@mui/material'
import {
    GridFooterContainer,
    GridPreferencePanelsValue,
    useGridApiContext,
} from '@mui/x-data-grid-pro'
import { GetCompaniesById } from 'candidate/graphql/generatedProfile.gql'
import { type FC, type ReactNode } from 'react'
import {
    GetFundSizeBucketOptions,
    GetIndustryOptions,
    GetSectorOptions,
    GetVisaStatusOptions,
} from 'recruiter/graphql/companies.gql'
import { useRosterSearch, useRosterSqlSearch } from 'recruiter/hooks'
import { type RosterFilterState } from 'shared/hooks/rosterFilterState'
import { startCase } from 'lodash'
import { GetOfficeLocationsById } from 'shared/components/OfficeLocationsAutocomplete/OfficeLocationsAutocomplete.gql'
import { GetRecruitersByUserIds } from 'shared/components/RecruitersAutocomplete/RecruitersAutocomplete.gql'
import { GetProcessStatuses } from 'shared/graphql/candidateProcessStatus.gql'
import { GetTitleOptions } from 'shared/graphql/titles.gql'
import {
    buildActiveChips,
    FilterPills,
    type FilterConfig,
    truncateNames,
    tooltipNames,
} from 'shared/components/FilterPills'
import { getFullName, getPxAsRem } from 'shared/utils'
import { RosterTableToolbar } from './RosterTableToolbar'

interface LookupData {
    companies: Array<{ id: string; value: string }>
    recruiters: Array<{
        id: string
        userId: string
        user: { firstName: string; lastName: string; nickname?: string | null }
    }>
    industries: Array<{ id: string; value: string }>
    sectors: Array<{ id: string; value: string }>
    locations: Array<{ id: string; value: string }>
    titles: Array<{ id: string; value: string }>
    fundSizeBuckets: Array<{ id: string; value: string }>
    visaStatuses: Array<{ id: string; value: string }>
    processStatuses: Array<{
        id: string
        statusLabel: string
        processStatusSummaryGroup?: { id: string; label: string } | null
    }>
}

const formatDateRange = (
    gte: string | undefined,
    lte: string | undefined
): string | null => {
    const formatDate = (iso: string | undefined): string => {
        if (!iso) return ''
        const d = new Date(iso)
        return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
    }
    const from = formatDate(gte)
    const to = formatDate(lte)
    if (from && to) return `${from} - ${to}`
    if (from) return `From ${from}`
    if (to) return `Until ${to}`
    return null
}

function buildFilterConfigs(
    defaultStatuses: string[]
): FilterConfig<RosterFilterState, LookupData>[] {
    return [
        {
            key: 'quickSearch',
            label: 'Search',
            isActive: (f) => !!f.quickSearch,
            getDisplayValue: (f) => f.quickSearch || null,
            getClearFields: () => ({ quickSearch: undefined }),
        },
        {
            key: 'companyIds',
            label: 'Company',
            isActive: (f) => f.companyIds.length > 0,
            getDisplayValue: (f, { companies }) => {
                const names = f.companyIds
                    .map((id) => companies.find((c) => c.id === id)?.value)
                    .filter(Boolean)
                return truncateNames(names, f.companyIds.length)
            },
            getTooltipValue: (f, { companies }) => {
                const names = f.companyIds
                    .map((id) => companies.find((c) => c.id === id)?.value)
                    .filter(Boolean)
                return tooltipNames(names)
            },
            getClearFields: () => ({ companyIds: [] }),
        },
        {
            key: 'createdByUserIds',
            label: 'Created By',
            isActive: (f) => f.createdByUserIds.length > 0,
            getDisplayValue: (f, { recruiters }) => {
                const names = f.createdByUserIds
                    .map((userId) => {
                        const r = recruiters.find((r) => r.userId === userId)
                        return r ? getFullName(r.user) : null
                    })
                    .filter(Boolean)
                return truncateNames(names, f.createdByUserIds.length)
            },
            getTooltipValue: (f, { recruiters }) => {
                const names = f.createdByUserIds
                    .map((userId) => {
                        const r = recruiters.find((r) => r.userId === userId)
                        return r ? getFullName(r.user) : null
                    })
                    .filter(Boolean)
                return tooltipNames(names)
            },
            getClearFields: () => ({ createdByUserIds: [] }),
        },
        {
            key: 'internalUserIds',
            label: 'Internal Users',
            isActive: (f) => f.internalUserIds.length > 0,
            getDisplayValue: (f, { recruiters }) => {
                const names = f.internalUserIds
                    .map((userId) => {
                        const r = recruiters.find((r) => r.userId === userId)
                        return r ? getFullName(r.user) : null
                    })
                    .filter(Boolean)
                return truncateNames(names, f.internalUserIds.length)
            },
            getTooltipValue: (f, { recruiters }) => {
                const names = f.internalUserIds
                    .map((userId) => {
                        const r = recruiters.find((r) => r.userId === userId)
                        return r ? getFullName(r.user) : null
                    })
                    .filter(Boolean)
                return tooltipNames(names)
            },
            getClearFields: () => ({ internalUserIds: [] }),
        },
        {
            key: 'industryIds',
            label: 'Industry',
            isActive: (f) => f.industryIds.length > 0,
            getDisplayValue: (f, { industries }) => {
                const names = f.industryIds
                    .map((id) => industries.find((i) => i.id === id)?.value)
                    .filter(Boolean)
                return truncateNames(names, f.industryIds.length)
            },
            getTooltipValue: (f, { industries }) => {
                const names = f.industryIds
                    .map((id) => industries.find((i) => i.id === id)?.value)
                    .filter(Boolean)
                return tooltipNames(names)
            },
            getClearFields: () => ({ industryIds: [] }),
        },
        {
            key: 'sectorIds',
            label: 'Sector',
            isActive: (f) => f.sectorIds.length > 0,
            getDisplayValue: (f, { sectors }) => {
                const names = f.sectorIds
                    .map((id) => sectors.find((s) => s.id === id)?.value)
                    .filter(Boolean)
                return truncateNames(names, f.sectorIds.length)
            },
            getTooltipValue: (f, { sectors }) => {
                const names = f.sectorIds
                    .map((id) => sectors.find((s) => s.id === id)?.value)
                    .filter(Boolean)
                return tooltipNames(names)
            },
            getClearFields: () => ({ sectorIds: [] }),
        },
        {
            key: 'geoLocationIds',
            label: 'Location',
            isActive: (f) => f.geoLocationIds.length > 0,
            getDisplayValue: (f, { locations }) => {
                const names = f.geoLocationIds
                    .map((id) => locations.find((l) => l.id === id)?.value)
                    .filter(Boolean)
                return truncateNames(names, f.geoLocationIds.length)
            },
            getTooltipValue: (f, { locations }) => {
                const names = f.geoLocationIds
                    .map((id) => locations.find((l) => l.id === id)?.value)
                    .filter(Boolean)
                return tooltipNames(names)
            },
            getClearFields: () => ({ geoLocationIds: [] }),
        },
        {
            key: 'geoLocationText',
            label: 'Location',
            isActive: (f) => f.geoLocationText.length > 0,
            getDisplayValue: (f) => {
                const names = f.geoLocationText.map((v) =>
                    v.startsWith(', ') ? v.slice(2) : v
                )
                return truncateNames(names, names.length)
            },
            getTooltipValue: (f) => {
                const names = f.geoLocationText.map((v) =>
                    v.startsWith(', ') ? v.slice(2) : v
                )
                return tooltipNames(names)
            },
            getClearFields: () => ({ geoLocationText: [] }),
        },
        {
            key: 'titleIds',
            label: 'Title',
            isActive: (f) => f.titleIds.length > 0,
            getDisplayValue: (f, { titles }) => {
                const names = f.titleIds
                    .map((id) => titles.find((t) => t.id === id)?.value)
                    .filter(Boolean)
                return truncateNames(names, f.titleIds.length)
            },
            getTooltipValue: (f, { titles }) => {
                const names = f.titleIds
                    .map((id) => titles.find((t) => t.id === id)?.value)
                    .filter(Boolean)
                return tooltipNames(names)
            },
            getClearFields: () => ({ titleIds: [] }),
        },
        {
            key: 'consultants',
            label: 'Consultants',
            isActive: (f) => !!f.consultants,
            getDisplayValue: (f) =>
                f.consultants === 'yes'
                    ? 'Yes'
                    : f.consultants === 'no'
                      ? 'No'
                      : null,
            getClearFields: () => ({ consultants: undefined }),
        },
        {
            key: 'fundSizeIds',
            label: 'Fund Size',
            isActive: (f) => f.fundSizeIds.length > 0,
            getDisplayValue: (f, { fundSizeBuckets }) => {
                const names = f.fundSizeIds
                    .map(
                        (id) => fundSizeBuckets.find((b) => b.id === id)?.value
                    )
                    .filter(Boolean)
                return truncateNames(names, f.fundSizeIds.length)
            },
            getTooltipValue: (f, { fundSizeBuckets }) => {
                const names = f.fundSizeIds
                    .map(
                        (id) => fundSizeBuckets.find((b) => b.id === id)?.value
                    )
                    .filter(Boolean)
                return tooltipNames(names)
            },
            getClearFields: () => ({ fundSizeIds: [] }),
        },
        {
            key: 'visaStatusIds',
            label: 'Visa',
            isActive: (f) => f.visaStatusIds.length > 0,
            getDisplayValue: (f, { visaStatuses }) => {
                const names = f.visaStatusIds
                    .map((id) => visaStatuses.find((v) => v.id === id)?.value)
                    .filter(Boolean)
                return truncateNames(names, f.visaStatusIds.length)
            },
            getTooltipValue: (f, { visaStatuses }) => {
                const names = f.visaStatusIds
                    .map((id) => visaStatuses.find((v) => v.id === id)?.value)
                    .filter(Boolean)
                return tooltipNames(names)
            },
            getClearFields: () => ({ visaStatusIds: [] }),
        },
        {
            key: 'statuses',
            label: 'Status',
            isActive: (f) =>
                f.statuses.length !== defaultStatuses.length ||
                !f.statuses.every((s) => defaultStatuses.includes(s)),
            getDisplayValue: (f) => {
                const names = f.statuses.map((s) => startCase(s))
                return truncateNames(names, names.length)
            },
            getTooltipValue: (f) => {
                const names = f.statuses.map((s) => startCase(s))
                return tooltipNames(names)
            },
            getClearFields: () => ({ statuses: defaultStatuses }),
        },
        {
            key: 'type',
            label: 'Type',
            isActive: (f) => !!f.type,
            getDisplayValue: (f) => f.type || null,
            getClearFields: () => ({ type: undefined }),
        },
        {
            key: 'createdAt',
            label: 'Created',
            isActive: (f) => !!f.createdAtGte || !!f.createdAtLte,
            getDisplayValue: (f) =>
                formatDateRange(f.createdAtGte, f.createdAtLte),
            getClearFields: () => ({
                createdAtGte: undefined,
                createdAtLte: undefined,
            }),
        },
        {
            key: 'startDates',
            label: 'Start Date',
            isActive: (f) => f.startDates.length > 0,
            getDisplayValue: (f) =>
                truncateNames(f.startDates, f.startDates.length),
            getTooltipValue: (f) => tooltipNames(f.startDates),
            getClearFields: () => ({ startDates: [] }),
        },
        {
            key: 'processStatus',
            label: 'Process Status',
            isActive: (f) =>
                f.processStatusIds.length > 0 ||
                f.processStatusGroupIds.length > 0,
            getDisplayValue: (f, { processStatuses }) => {
                const groupNames = f.processStatusGroupIds.map((groupId) => {
                    const ps = processStatuses.find(
                        (ps) => ps.processStatusSummaryGroup?.id === groupId
                    )
                    return ps?.processStatusSummaryGroup?.label
                })
                const statusNames = f.processStatusIds.map(
                    (id) =>
                        processStatuses.find((ps) => ps.id === id)?.statusLabel
                )
                const allNames = [...groupNames, ...statusNames]
                return truncateNames(
                    allNames,
                    f.processStatusIds.length + f.processStatusGroupIds.length
                )
            },
            getTooltipValue: (f, { processStatuses }) => {
                const groupNames = f.processStatusGroupIds.map((groupId) => {
                    const ps = processStatuses.find(
                        (ps) => ps.processStatusSummaryGroup?.id === groupId
                    )
                    return ps?.processStatusSummaryGroup?.label
                })
                const statusNames = f.processStatusIds.map(
                    (id) =>
                        processStatuses.find((ps) => ps.id === id)?.statusLabel
                )
                return tooltipNames([...groupNames, ...statusNames])
            },
            getClearFields: () => ({
                processStatusIds: [],
                processStatusGroupIds: [],
            }),
        },
    ]
}

export const RosterTableFooterSlot: FC = () => {
    const apiRef = useGridApiContext()
    const { tableState, setTableState, totalItems } = useRosterSearch()
    const allColumns = apiRef.current.getAllColumns()
    const visibleColumns = apiRef.current.getVisibleColumns()
    const showCount = visibleColumns.length < allColumns.length

    return (
        <GridFooterContainer>
            <Button
                variant="text"
                size="small"
                onClick={() =>
                    apiRef.current.showPreferences(
                        GridPreferencePanelsValue.columns
                    )
                }
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
                Columns
                {showCount &&
                    ` (${visibleColumns.length}/${allColumns.length})`}
            </Button>
            <Box flex={1} />
            <TablePagination
                component="div"
                count={totalItems}
                page={tableState.page - 1}
                rowsPerPage={tableState.limit}
                onPageChange={(_, newPage) => {
                    if (totalItems > 0) {
                        setTableState({ page: newPage + 1 })
                    }
                }}
                onRowsPerPageChange={(e) =>
                    setTableState({
                        limit: parseInt(e.target.value, 10),
                        page: 1,
                    })
                }
                rowsPerPageOptions={[25, 50, 100]}
            />
        </GridFooterContainer>
    )
}

interface RosterTableToolbarSlotProps {
    rightSlot?: ReactNode
    selectedCount?: number
}

export const RosterTableToolbarSlot: FC<RosterTableToolbarSlotProps> = ({
    rightSlot,
    selectedCount = 0,
}) => {
    const {
        tableState,
        setTableState,
        hasActiveFilters,
        clearFilters,
        totalItems,
        defaultStatuses,
        selectAllRosters,
        isSelectingAll,
        setSelectedRosterIds,
        allSelectableIds,
    } = useRosterSqlSearch()
    const selectableTotal = allSelectableIds?.length ?? totalItems
    const filters = tableState.filters
    const setFilters = (newFilters: RosterFilterState) =>
        setTableState({ filters: newFilters })
    const filterConfigs = buildFilterConfigs(defaultStatuses)

    const userIds = [...filters.createdByUserIds, ...filters.internalUserIds]

    const { data: companiesData } = useQuery(GetCompaniesById, {
        variables: { ids: filters.companyIds },
        skip: filters.companyIds.length === 0,
    })
    const { data: recruitersData } = useQuery(GetRecruitersByUserIds, {
        variables: { ids: userIds },
        skip: userIds.length === 0,
    })
    const { data: industriesData } = useQuery(GetIndustryOptions)
    const { data: sectorsData } = useQuery(GetSectorOptions)
    const { data: titlesData } = useQuery(GetTitleOptions)
    const { data: locationsData } = useQuery(GetOfficeLocationsById, {
        variables: { ids: filters.geoLocationIds },
        skip: filters.geoLocationIds.length === 0,
    })
    const { data: fundSizeBucketsData } = useQuery(GetFundSizeBucketOptions)
    const { data: visaStatusesData } = useQuery(GetVisaStatusOptions)
    const { data: processStatusesData } = useQuery(GetProcessStatuses)

    const lookupData: LookupData = {
        companies: companiesData?.companiesById ?? [],
        recruiters: recruitersData?.recruitersByUserId ?? [],
        industries: industriesData?.industries ?? [],
        sectors: sectorsData?.sectors ?? [],
        locations: locationsData?.officeLocationsById ?? [],
        titles: titlesData?.titles ?? [],
        fundSizeBuckets: fundSizeBucketsData?.fundSizeBuckets ?? [],
        visaStatuses: visaStatusesData?.visaStatuses ?? [],
        processStatuses: processStatusesData?.processStatuses ?? [],
    }

    const activeChips = buildActiveChips(
        filterConfigs,
        filters,
        lookupData,
        setFilters
    )

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={getPxAsRem(8)}
            flexWrap="wrap"
            paddingBlockStart={getPxAsRem(6)}
            paddingBlockEnd={getPxAsRem(12)}
        >
            <RosterTableToolbar />
            <FilterPills chips={activeChips} />
            <Box flex={1} />
            {hasActiveFilters && (
                <Button
                    variant="text"
                    size="small"
                    onClick={clearFilters}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                    Clear all
                </Button>
            )}

            {selectedCount > 0 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                        pl: 1.5,
                        ml: 0.5,
                    }}
                >
                    {selectedCount < selectableTotal && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={selectAllRosters}
                            disabled={isSelectingAll}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                minWidth: 'auto',
                            }}
                        >
                            {isSelectingAll ? (
                                <>
                                    <CircularProgress
                                        size={12}
                                        sx={{ mr: 0.5 }}
                                    />
                                    Selecting...
                                </>
                            ) : (
                                `Select all ${selectableTotal.toLocaleString()} rosters`
                            )}
                        </Button>
                    )}
                    {selectedCount >= selectableTotal && (
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                            }}
                        >
                            All {selectableTotal.toLocaleString()} selected
                        </Typography>
                    )}
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => setSelectedRosterIds([])}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            color: 'text.secondary',
                        }}
                    >
                        Clear ({selectedCount.toLocaleString()})
                    </Button>
                </Stack>
            )}

            {rightSlot}
        </Stack>
    )
}
